import Debug from '@/util/debug';
import clone from 'clone';
import numeral from 'numeral';
import _ from 'lodash';
import { flatten } from 'flat';
import tinycolor from 'tinycolor2';
import KeyCombo from '@/directives/key-combo';
// eslint-disable-next-line import/no-webpack-loader-syntax
import rawCss from '!!raw-loader!sass-loader?outputStyle=compact!./dynamic-style/style.scss';
import { DEFAULT_FAVICON_URL, SW_MESSAGE_SOURCE } from '@/util/constants';
import Loading from '@/components/loading/Loading.vue';
import Promise from 'bluebird';
import idb from '@/util/indexed-db';
import { isOfflineModeSupported } from '@/util/helper';
import DynamicStyle from './dynamic-style/DynamicStyle.vue';
import ColorPicker from './color-picker/ColorPicker.vue';
import * as StaticDesign from './static-design.js';

const debug = Debug('initializer');

export default {
  dependencies: [
    'publicConfigService',
    'initializerService',
    'offlineModeService',
    'localeService',
  ],
  components: {
    'dynamic-style': DynamicStyle,
    loading: Loading,
    'color-picker': ColorPicker,
  },
  props: ['loading'],
  directives: [
    KeyCombo,
  ],
  data() {
    return {
      config: {},
      error: null,
      clientSwitchHidden: true,
      client: 'default',
      defaultDesign: StaticDesign.getDefaultDesign(),
      allDesigns: StaticDesign.getAllDesigns(),
      designClone: {}, // TODO - temporary solution for all-client coloring
      test: rawCss.match(/([^\w\d-])--([^,\s)]*)/g),
    };
  },
  methods: {
    setFavicon(url) {
      debug.info('setFavicon: %s', url);
      let iconNode = document.querySelector('head link[rel=icon]');
      if (!iconNode) {
        iconNode = document.createElement('link');
        iconNode.rel = 'icon';
        iconNode.type = 'image/x-icon';
        document.querySelector('head').appendChild(iconNode);
      }

      iconNode.href = url;
    },
    addFamUrl(url) {
      // TODO - temporary solution while we don't know what will be given
      if (!/^https?:\/\//i.test(url)) {
        return `${this.config.fam_url}/${url}`;
      }
      return url;
    },
    onMessage(event) {
      debug.info('onMessage', event);
      if (event.data && event.data.source === SW_MESSAGE_SOURCE) {
        const data = event.data.payload;
        if (data.error) {
          this.$toast.warn({ message: this.$t(data.error) });
        } else if (data.newStatus !== this.isOnline) {
          this.offlineModeService.setStatus(data.newStatus);
          window.location.reload(true);
        }
      }
    },
  },
  computed: {
    clientDesign() {
      return this.client === 'default' && this.config.agent && this.config.agent.design && this.config.agent.design.colors
        ? this.config.agent.design : this.allDesigns[this.client];
    },
    designCompiled() {
      // This creates colors for: *-hover, *-text, *-text-hover
      // Combine default colors with client colors
      const design = StaticDesign.mergeDesigns(this.clientDesign);
      const newColors = {};
      Object.keys(design.colors).forEach(key => {
        if (!key.endsWith('-hover') && !key.endsWith('-text') && !key.endsWith('-text-hover')) {
          if (design.colors[key] !== '') {
            const color = tinycolor(design.colors[key]);
            let colorHover = color.clone();
            if (!Object.prototype.hasOwnProperty.call(design.colors, `${key}-hover`)) {
              colorHover = color.getBrightness() < 50
                            ? color.clone().lighten(20)
                            : color.clone().darken(10);
              newColors[`${key}-hover`] = colorHover.toHexString();
            }
            if (!Object.prototype.hasOwnProperty.call(design.colors, `${key}-text`)) {
              // Create darker text for target box bottom (potential reach)
              if (key === 'color-target-bottom') {
                newColors[`${key}-text`] = color.clone().darken(20).toHexString();
              // Change header menu text color - TODO improve, because on some clients ir looks bad
              } else if (key === 'color-background-header' && color.getBrightness() === 255) {
                newColors[`${key}-text`] = '#bbb';
              } else {
                newColors[`${key}-text`] = color.getBrightness() < 180 ? '#fff' : '#000';
              }
            }
            if (!Object.prototype.hasOwnProperty.call(design.colors, `${key}-text-hover`)) {
              newColors[`${key}-text-hover`] = colorHover.getBrightness() < 180 ? '#fff' : '#000';
            }
          }
        }
      });

      // Combine created colors with default/client colors
      design.colors = Object.assign({}, newColors, design.colors);
      return design;
    },
  },
  mounted() {
    this.initializerService.configure()
      .then(() => this.publicConfigService.getConfig())
      .then(c => {
        this.config = c;
        if (this.config.agent.design.logo) {
          this.config.agent.design.logo = this.addFamUrl(this.config.agent.design.logo);
        }
        this.designClone = clone(c.agent.design);
        const disabledLanguages = _.get(c, 'agent.disabled_locales', []);
        let interfaceLanguages = c.interface_languages || [];
        interfaceLanguages = interfaceLanguages.filter(l => !disabledLanguages.includes(l));

        this.localeService.setLanguages(interfaceLanguages);
        return this.localeService.setUserLanguage();
      })
      .then(() => {
        let favicon = DEFAULT_FAVICON_URL;
        if (this.config.agent.design.favicon) {
          favicon = this.addFamUrl(this.config.agent.design.favicon);
        }
        this.setFavicon(favicon);
      })
      .then(() => this.localeService.getLocales())
      .then(localeStrings => {
        const readyLocaleStrings = {};
        const defaultEnglish = flatten(this.$i18n.i18next.getResourceBundle('en'));
        Object.keys(localeStrings).forEach(langCode => {
          readyLocaleStrings[langCode] = {
            ..._.cloneDeep(defaultEnglish),
            ..._.pickBy(_.get(localeStrings, langCode, {}), (val, key) => !key.startsWith(')')),
            ..._.cloneDeep(_.get(this, 'config.common_translations', {})),
          };
        });
        this.localeService.updateTranslations(readyLocaleStrings);
      })
      .then(() => {
        // Add other necessary translations
        this.localeService.updateTranslations(
          this.localeService.getLanguages().reduce(
            (res, lng) => {
              res[lng] = {};
              res[lng]['_internal.currency_symbol'] = this.config.currency_symbol;

              // Deletion is needed because you can't redefine locales normally
              // Tested with numeral 2.0.6
              // See also: https://github.com/adamwdraper/Numeral-js/issues/503
              delete numeral.locales[lng];
              numeral.register('locale', lng, {
                delimiters: {
                  thousands: this.config.delimiters.thousands,
                  decimal: this.config.delimiters.decimal,
                },
                abbreviations: {
                  thousand: 'k',
                  million: 'm',
                  billion: 'b',
                  trillion: 't',
                },
                currency: {
                  symbol: this.config.currency_symbol,
                },
              });
              return res;
            },
            {},
          ),
        );
      })
      .then(() => {
        document.title = this.config.agent.title || 'Agent';
        window.history.replaceState({}, document.title, document.location.href);
      })
      .then(() => this.$emit('update:loading', false))
      .catch(err => {
        this.error = String(err);
        debug.error('error: %o', err);
      });

    this.$on('keycombo::success', () => {
      this.clientSwitchHidden = !this.clientSwitchHidden;
    });

    if (isOfflineModeSupported()) {
      navigator.serviceWorker.getRegistrations()
        .then(registrations => {
          // Unregister all service workers
          const promises = [];
          registrations.forEach(r => promises.push(r.unregister()));
          if (promises.length > 0) {
            debug.info('unregistering service worker');
            Promise.all(promises)
              .then(() => {
                // Clear cache and IndexedDB
                Promise.all([
                  global.caches.keys()
                    .then(keys => {
                      const tmp = [];
                      keys.forEach(k => tmp.push(global.caches.delete(k)));
                      return Promise.all(tmp);
                    }),
                  idb.delete(),
                ]).finally(() => {
                  debug.info('reloading page after uninstall');
                  window.location.reload(true);
                });
              });
          } else {
            debug.info('service worker already unregistered');
          }
        });
    } else {
      debug.warn('cannot use service worker');
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/initializer/Initializer.js